<template>
  <div class="container ai-chat-popup" style="bottom: 48px; left: 16px; position: fixed; z-index: 9999999999; max-width: 640px; width: calc(100vw - 64px); max-height: calc(100vh - 192px); overflow: auto; padding: 0;">
    <div class="ai-chat-header flex between p8 b8" style="background: #111; border-bottom: 1px solid #222;">
      <div class="flex align-center">
        <i class="fa fa-robot fa-lg mr8"/>
        <b class="sm-font">AI Chat</b>
      </div>
      <div class="flex align-center">
        <select v-model="$store.state.terminal.selectedModel" class="sm-input r8" style="margin-right:8px">
          <option v-for="m in $store.state.terminal.availableModels || []" :key="m" :value="m">{{ m }}</option>
        </select>
        <button @click="$store.dispatch('agent/getOllamaModels')" class="xs-font">↻</button>
        <a class="ml8" href @click.prevent="$set($store.state.show, 'aichat', false)">✕</a>
      </div>
    </div>

    <div class="ai-chat-body p8 v-scroll" style="background: #0f0f0f; min-height: 180px;">
      <!-- Conversation / Messages -->
      <div v-if="conversation" :key="conversation.id" class="ai-conversation">
        <div v-for="(b, ts) in sortedBanter" :key="ts" class="ai-banter-entry p8 b4 mb8" :class="{'ai-from-ai': b.userId === $store.state.agent.agentUserId || b.userId === $store.state.ai.config.agentId, 'ai-from-user': b.userId === $store.state.auth.uid}">
          <div class="xs-font" style="opacity:.8">{{ userLabel(b.userId) }} — <span class="muted">{{ fmtTime(ts) }}</span></div>
          <div class="p8" style="white-space:pre-wrap">
            <template v-if="isJson(b.text)">
              <div>{{ prettyResponse(b.text) }}</div>
              <!-- Render action buttons if function is defined in response -->
              <div v-if="getActionButton(b.text)" class="mt8">
                <component :is="'action'" :fun="getActionButton(b.text).fun" :name="getActionButton(b.text).name" />
              </div>
            </template>
            <template v-else>
              {{ b.text }}
            </template>
            <div v-if="parsedViewScope(b.text)" class="xs-font muted mt8">View scope: {{ parsedViewScope(b.text).count }} elements — <a href="#" @click.prevent="showViewScope(parsedViewScope(b.text))">inspect</a></div>
            <!-- Feedback loop indicator -->
            <div v-if="isInFeedbackLoop" class="mt8 flex align-center">
              <button @click="stopFeedbackLoop" class="stop-btn" title="Stop feedback loop">
                <i class="fa fa-stop-circle"></i> Stop
              </button>
              <span class="ml8 xs-font muted">AI is processing feedback loop...</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="p16 muted">No AI conversation selected — open or create one from messages.</div>
    </div>

    <div class="ai-chat-controls p8 flex between" style="background: linear-gradient(180deg,#0a0a0a,#060606); border-top:1px solid #222;">
      <div style="flex:1; margin-right:8px">
        <textarea v-model="input" class="sm-input" placeholder="Ask the app helper... (Ctrl+Enter to send)" @keydown.enter.ctrl="send()" style="width:100%; min-height:48px"></textarea>
      </div>
      <div style="width:200px; display:flex; flex-direction:column; gap:8px;">
        <label class="xs-font">Behavior</label>
        <div class="flex column">
          <label class="xs-font"><input type="checkbox" v-model="allowExecution" /> Allow agent to propose actions (requires manual confirm)</label>
          <label class="xs-font"><input type="checkbox" v-model="attachScreenshot" /> Attach screenshot</label>
          <label class="xs-font"><input type="checkbox" v-model="attachScope" /> Attach view scope</label>
        </div>
        <button class="send-btn" @click="send">Send</button>
      </div>
    </div>
  </div>
</template>

<script>
import action from '../widgets/action.vue'

export default {
  name: 'aichat',
  components: {
    action
  },
  data() {
    return {
      input: '',
      allowExecution: false,
      attachScreenshot: false,
      attachScope: true,
      isInFeedbackLoop: false,
      conversationCache: null,
    }
  },
  computed: {
    conversationId() {
      return this.$store.state.terminal.currentConversationId
    },
    conversation() {
      const convId = this.conversationId
      const msgs = this.$store.state.msgs
      if (!convId || !msgs) return null
      return msgs[convId] || null
    },
    sortedBanter() {
      if (!this.conversation || !this.conversation.banter) return {}
      const entries = Object.entries(this.conversation.banter || {}).sort((a,b)=>Number(a[0]) - Number(b[0]))
      const out = {}
      entries.forEach(([k,v]) => out[k] = v)
      return out
    },
  },
  watch: {
    '$store.state.msgs': {
      handler(newMsgs) {
        const convId = this.conversationId
        if (convId && newMsgs && newMsgs[convId]) {
          this.$set(this, 'conversationCache', newMsgs[convId])
          this.$forceUpdate()
          try {
            const convo = newMsgs[convId]
            if (convo && convo.banter) {
              // find latest banter entry
              const keys = Object.keys(convo.banter || {}).map(k=>Number(k)).sort((a,b)=>b-a)
              if (keys && keys.length) {
                const latestKey = String(keys[0])
                const latest = convo.banter[latestKey]
                // If latest message is authored by the agent user, process it
                const agentId = this.$store.state.agent && this.$store.state.agent.agentUserId
                if (latest && latest.userId && (latest.userId === agentId || latest.userId === (this.$store.state.ai && this.$store.state.ai.config && this.$store.state.ai.config.agentId))) {
                  // Try to process AI response (e.g., function suggestions)
                  try { this.processAIResponse(latest.text) } catch (e) { /* ignore */ }
                }
              }
            }
          } catch (e) { /* ignore */ }
        } else {
          this.conversationCache = null
        }
      },
      deep: true,
      immediate: true
    },
    conversationId: {
      handler(newId) {
        if (newId && this.$store.state.msgs && this.$store.state.msgs[newId]) {
          this.$set(this, '_conversation', this.$store.state.msgs[newId])
        } else {
          this.$set(this, 'conversationCache', null)
        }
        this.$forceUpdate()
      },
      immediate: true
    },
    '$store.state.terminal.currentConversationId': {
      handler(newId) {
        if (newId && this.$store.state.msgs && this.$store.state.msgs[newId]) {
          this.$set(this, '_conversation', this.$store.state.msgs[newId])
        } else {
          this.$set(this, 'conversationCache', null)
        }
        this.$forceUpdate()
      },
      immediate: true
    },
    '$store.state.terminal.lastPlan': {
      handler(newPlan) {
        if (newPlan && newPlan.feedbackLoop) {
          this.isInFeedbackLoop = true
        } else {
          this.isInFeedbackLoop = false
        }
      },
      deep: true
    }
  },
  methods: {
    userLabel(uid) {
      if (!uid) return 'system'
      if (uid === this.$store.state.agent.agentUserId) return 'AI'
      if (uid === this.$store.state.auth.uid) return 'You'
      const user = this.$store.state.user && this.$store.state.user[uid]
      return (user && (user.displayName || user.name)) || uid
    },
    fmtTime(ts) {
      try { return new Date(Number(ts)).toLocaleTimeString() } catch (e) { return ts }
    },
    async send() {
      const message = (this.input || '').trim()
      if (!message) return

      // Check if user is logged in
      if (!this.$store.state.auth.uid) {
        alert('Please log in to use AI chat')
        return
      }

        // Ensure conversation exists and is created as 'ai-chat' for this UI
        if (!this.conversationId) {
          const newId = await this.$store.dispatch('agent/createConversation', { type: 'ai-chat' })
          if (!newId) {
            alert('Failed to create conversation. Please ensure you are logged in.')
            return
          }
        } else {
          // If conversation exists but isn't ai-chat, ensure it's flagged
          const conv = this.conversation
          if (conv && conv.type !== 'ai-chat') {
            // create a new ai-chat conversation and switch to it
            const newId = await this.$store.dispatch('agent/createConversation', { type: 'ai-chat' })
            if (newId) {
              this.$store.commit('set', { keys: ['terminal', 'currentConversationId'], val: newId }, { root: true })
            } else {
              alert('Failed to create conversation. Please ensure you are logged in.')
              return
            }
          }
        }

      // Prepare context helpers (strict and small by default)
      const context = { attachScope: this.attachScope }
      if (this.attachScreenshot && window.agentLayer) context.screenshot = await window.agentLayer.takeScreenshot().catch(()=>null)
      if (this.attachScope && window.agentLayer) context.scope = window.agentLayer.discoverViewScope()

      // Add user message to conversation and let the shared agent flow handle AI response
      await this.$store.dispatch('agent/sendUserMessage', { message, phase: 'intent' })

      // Optionally attach context as separate context item for the agent introspection
      if (context.scope || context.screenshot) {
        // Use lightweight commit -- store contexts in terminal.context as an array
        const items = (Array.isArray(this.$store.state.terminal.context) ? this.$store.state.terminal.context.slice() : [])
        items.push({ type: 'view_scope', value: context.scope || null, screenshot: context.screenshot || null, timestamp: Date.now() })
        this.$store.commit('set', { keys: ['terminal', 'context'], val: items }, { root: true })
      }

      // Clear input
      this.input = ''
    },
    isJson(text) {
      if (!text || typeof text !== 'string') return false
      // Skip if text looks like an error message
      if (text.includes('is not valid JSON') || text.includes('SyntaxError')) return false
      try { JSON.parse(text); return true } catch (e) { return false }
    },
    prettyResponse(text) {
      try {
        const p = JSON.parse(text)
        return p.response || JSON.stringify(p, null, 2)
      } catch (e) {
        return text
      }
    },
    parsedViewScope(text) {
      try {
        const p = typeof text === 'string' ? JSON.parse(text) : text
        if (p && p.view_scope) {
          const vs = p.view_scope
          return { count: (vs.elements && vs.elements.length) || vs.count || 0, data: vs }
        }
      } catch (e) {
        // Ignore parse errors
      }
      return null
    },
    showViewScope(vs) {
      // For now open a window with JSON view to inspect quickly
      const modal = window.open('', '_blank')
      if (modal) modal.document.write('<pre>' + JSON.stringify(vs, null, 2) + '</pre>')
    },
    getActionButton(text) {
      try {
        const parsed = typeof text === 'string' ? JSON.parse(text) : text
        if (parsed && parsed.function && parsed.function.name) {
          return {
            fun: parsed.function.name,
            name: parsed.function.name
          }
        }
        if (parsed && parsed.actionButton) {
          return parsed.actionButton
        }
      } catch (e) {
        // Ignore parse errors
      }
      return null
    },
    async stopFeedbackLoop() {
      if (window.agentLayer && window.agentLayer.stopFeedbackLoop) {
        await window.agentLayer.stopFeedbackLoop()
      }
      this.isInFeedbackLoop = false
      this.$store.commit('set', { keys: ['terminal', 'lastPlan', 'feedbackLoop'], val: false }, { root: true })
    },
    async processAIResponse(text) {
      try {
        const parsed = typeof text === 'string' ? JSON.parse(text) : text
        if (parsed && parsed.function) {
          // Use the store action to create function (handles all the logic)
          await this.$store.dispatch('agent/createFunctionFromAI', { functionDef: parsed.function })
        }
      } catch (e) {
        // Not JSON or no function, skip
        console.debug('[aichat] processAIResponse error:', e)
      }
    }
  },
  async mounted() {
    // Expose a small, friendly alias for the agent layer (if available)
    if (window && !window.agentLayer) {
      try {
        window.agentLayer = require('../../agent/layer.js').default.init(window.$store)
      } catch (e) {
        // debug & continue — best-effort agent layer initialization
        console.debug('aichat: agentLayer init failed', e)
      }
    }
      // ensure a chat conversation exists for ai-chat UI
      this.$nextTick(async () => {
        try {
          if (!this.conversationId) {
            // Prefer layer helper if available
            if (window && window.agentLayer && window.agentLayer.createConversation) {
              const id = await window.agentLayer.createConversation({ type: 'ai-chat' })
              if (id) this.$store.commit('set', { keys: ['terminal', 'currentConversationId'], val: id }, { root: true })
            } else {
              const id = await this.$store.dispatch('agent/createConversation', { type: 'ai-chat' })
              if (id) this.$store.commit('set', { keys: ['terminal', 'currentConversationId'], val: id }, { root: true })
            }
          }
          // Initialize conversation from store
          const convId = this.conversationId
          if (convId && this.$store.state.msgs && this.$store.state.msgs[convId]) {
            this.$set(this, '_conversation', this.$store.state.msgs[convId])
          }
        } catch (e) {
          // non-fatal
        }
      })
    // Ensure conversation is loaded
    if (!this.conversationId) {
      await this.$store.dispatch('agent/createConversation')
    }
    
    // Watch for new messages and create functions if needed
    this.$watch('sortedBanter', (newBanter) => {
      if (!newBanter) return
      const entries = Object.entries(newBanter).sort((a,b) => Number(b[0]) - Number(a[0]))
      for (const [, b] of entries) {
        if ((b.userId === this.$store.state.ai.config.agentId || b.userId === '5nA8rryyPedDsIvtWmTD0pRbann2') && b.text) {
          this.processAIResponse(b.text)
          break // Only process the latest AI response
        }
      }
    }, { deep: true, immediate: true })
  }
}
</script>

<style scoped>
.ai-chat-popup{ border-radius: 8px; box-shadow: 0 6px 40px rgba(0,0,0,.8); background: #0b0b0b; color: #ddd }
.ai-chat-header b { color: #fff }
.ai-banter-entry { border: 1px solid rgba(255,255,255,0.03); border-radius:6px }
.ai-from-ai { background: linear-gradient(90deg, rgba(35,40,40,0.6), rgba(18,18,18,0.5)) }
.ai-from-user { background: linear-gradient(90deg, rgba(18,24,42,0.4), rgba(12,12,12,0.25)) }
.stop-btn {
  padding: 4px 8px;
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.stop-btn:hover {
  background: #ff6666;
}
</style>
